import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import { cn } from "@/lib/utils";
import { PANEL_CLASS } from "../report-styles";

export interface ReportColumn<Row> {
  header: string;
  cell: (row: Row) => React.ReactNode;
  numeric?: boolean;
  total?: React.ReactNode;
}

interface Props<Row> {
  label: string;
  columns: ReportColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  totalLabel: string;
}

const NUMERIC = "text-right tabular-nums";

export const ReportTable = <Row,>({ label, columns, rows, rowKey, totalLabel }: Props<Row>) => {
  const firstTotal = columns.findIndex(column => column.total !== undefined);
  return (
    <Table aria-label={label} className="min-w-[960px]" containerClassName={PANEL_CLASS}>
      <TableHeader className="bg-spend-header">
        <TableRow>
          <TableHead>#</TableHead>
          {columns.map(column => (
            <TableHead key={column.header} className={cn(column.numeric && "text-right")}>
              {column.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={rowKey(row)}>
            <TableCell className="text-gray-500">{index + 1}</TableCell>
            {columns.map(column => (
              <TableCell key={column.header} className={cn(column.numeric && NUMERIC)}>
                {column.cell(row)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {firstTotal >= 0 ? (
        <TableFooter className="border-t-2 border-spend-ink font-semibold text-spend-ink">
          <TableRow>
            <TableHead scope="row" colSpan={firstTotal + 1} className="text-spend-ink">
              {totalLabel}
            </TableHead>
            {columns.slice(firstTotal).map(column => (
              <TableCell key={column.header} className={cn(column.numeric && NUMERIC)}>
                {column.total ?? null}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      ) : null}
    </Table>
  );
};
